import { memo, useCallback, useRef, useState } from 'react';
import {
  SCRATCH_BLOCK_TYPES,
  SCRATCH_TYPE_LABEL,
  type ScratchBlock,
  type ScratchBlockType,
} from '../../../shared/scratch';
import { TypeChip, TypeChipButton } from './TypeChip';
import ScratchBody from './ScratchBody';
import PromoteMenu, { type PromoteAction } from './PromoteMenu';
import MenuSurface, { MenuDivider, MenuLabel, MenuRow } from './Menu';
import { scratchBodySummary } from './scratchText';

type OpenMenu = 'type' | 'more' | 'more-type' | 'promote' | null;

interface Props {
  block: ScratchBlock;
  index: number;
  total: number;
  dragging: boolean;
  editable: boolean;
  actionsDisabled: boolean;
  onChangeType: (id: string, type: ScratchBlockType) => void;
  onChangeBody: (id: string, body: unknown) => void;
  onPromote: (id: string, action: PromoteAction) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
  onMove: (id: string, delta: number) => void;
  onDragStart: (index: number) => void;
  onDrop: (index: number) => void;
  onDragEnd: () => void;
}

function ScratchBlockCard({
  block,
  index,
  total,
  dragging,
  editable,
  actionsDisabled,
  onChangeType,
  onChangeBody,
  onPromote,
  onDuplicate,
  onDelete,
  onRestore,
  onMove,
  onDragStart,
  onDrop,
  onDragEnd,
}: Props) {
  const [menu, setMenu] = useState<OpenMenu>(null);
  const typeRef = useRef<HTMLDivElement>(null);
  const promoteRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const used = block.used_at !== null;
  const summary = scratchBodySummary(block.body, block.type);

  function pickType(type: ScratchBlockType) {
    onChangeType(block.id, type);
    setMenu(null);
  }

  return (
    <div
      data-type={block.type}
      data-used={used}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(index);
      }}
      className={`
        hsp-scratch-card relative rounded-sm border bg-card px-2 pt-1.5 pb-2
        transition-[border-color,opacity,box-shadow] duration-150
        ${dragging ? 'opacity-40 border-accent' : 'border-line hover:border-fg/25'}
        ${used ? 'border-dashed bg-elevated opacity-70' : ''}
      `}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <button
          type="button"
          draggable={!actionsDisabled}
          disabled={actionsDisabled}
          onDragStart={(e) => {
            if (actionsDisabled) {
              e.preventDefault();
              return;
            }
            // Firefox refuses to start a drag with an empty dataTransfer; the
            // payload itself is unused, reordering runs off the pane's state.
            e.dataTransfer.setData('text/plain', String(index));
            e.dataTransfer.effectAllowed = 'move';
            onDragStart(index);
          }}
          onDragEnd={onDragEnd}
          onKeyDown={(e) => {
            if (actionsDisabled) return;
            if (e.key === 'ArrowUp' && index > 0) {
              e.preventDefault();
              onMove(block.id, -1);
            } else if (e.key === 'ArrowDown' && index < total - 1) {
              e.preventDefault();
              onMove(block.id, 1);
            }
          }}
          aria-label={`Reorder block ${index + 1} of ${total}: ${summary}`}
          title="Drag to reorder, or use the arrow keys"
          className="
            shrink-0 flex items-center text-line cursor-grab active:cursor-grabbing
            hover:text-faint transition-colors duration-150
          "
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx="9" cy="6" r="1.4" />
            <circle cx="15" cy="6" r="1.4" />
            <circle cx="9" cy="12" r="1.4" />
            <circle cx="15" cy="12" r="1.4" />
            <circle cx="9" cy="18" r="1.4" />
            <circle cx="15" cy="18" r="1.4" />
          </svg>
        </button>

        <div ref={typeRef} className="relative">
          <TypeChipButton
            type={block.type}
            open={!actionsDisabled && menu === 'type'}
            disabled={actionsDisabled}
            onClick={() => setMenu((m) => (m === 'type' ? null : 'type'))}
          />
          {!actionsDisabled && menu === 'type' && (
            <MenuSurface align="left" width={168} containerRef={typeRef} onClose={closeMenu}>
              <MenuLabel>Change type</MenuLabel>
              <TypeRows current={block.type} onPick={pickType} />
            </MenuSurface>
          )}
        </div>

        <span className="flex-1" />

        {!used && (
          <div ref={promoteRef} className="relative">
            <button
              type="button"
              disabled={actionsDisabled}
              onClick={() => setMenu((m) => (m === 'promote' ? null : 'promote'))}
              aria-haspopup="menu"
              aria-expanded={!actionsDisabled && menu === 'promote'}
              title="Send this block to the script"
              className={`
                inline-flex items-center h-6 px-1.5 rounded-sm border
                text-[11px] font-semibold whitespace-nowrap
                transition-colors duration-150
                ${
                  menu === 'promote'
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-line text-muted hover:border-accent hover:text-accent'
                }
              `}
            >
              → Script
            </button>
            {!actionsDisabled && menu === 'promote' && (
              <PromoteMenu
                containerRef={promoteRef}
                onClose={closeMenu}
                onPick={(action) => {
                  setMenu(null);
                  onPromote(block.id, action);
                }}
              />
            )}
          </div>
        )}

        <div ref={moreRef} className="relative">
          <button
            type="button"
            disabled={actionsDisabled}
            onClick={() => setMenu((m) => (m === 'more' || m === 'more-type' ? null : 'more'))}
            aria-haspopup="menu"
            aria-expanded={!actionsDisabled && (menu === 'more' || menu === 'more-type')}
            aria-label={`Block actions: ${summary}`}
            title="Block actions"
            className={`
              w-6 h-6 rounded-sm inline-flex items-center justify-center leading-none
              transition-colors duration-150
              ${
                menu === 'more' || menu === 'more-type'
                  ? 'bg-elevated text-fg'
                  : 'text-faint hover:bg-elevated hover:text-fg'
              }
            `}
          >
            <span aria-hidden>⋯</span>
          </button>

          {!actionsDisabled && menu === 'more' && (
            <MenuSurface width={196} containerRef={moreRef} onClose={closeMenu}>
              <MenuRow onClick={() => setMenu('more-type')}>
                <MenuIcon>
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                </MenuIcon>
                Change type
                <span className="ml-auto text-faint text-[11px]" aria-hidden>
                  ▸
                </span>
              </MenuRow>
              <MenuRow
                onClick={() => {
                  setMenu(null);
                  onDuplicate(block.id);
                }}
              >
                <MenuIcon>
                  <rect x="9" y="9" width="12" height="12" rx="2" />
                  <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                </MenuIcon>
                Duplicate
              </MenuRow>
              <MenuDivider />
              <MenuRow
                danger
                onClick={() => {
                  setMenu(null);
                  onDelete(block.id);
                }}
              >
                <MenuIcon>
                  <path d="M3 6h18" />
                  <path d="M8 6V4h8v2" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                </MenuIcon>
                Delete
              </MenuRow>
              <p className="px-3 pt-1 text-[11px] text-faint leading-snug">
                Delete is undoable from the toast.
              </p>
            </MenuSurface>
          )}

          {!actionsDisabled && menu === 'more-type' && (
            <MenuSurface width={196} containerRef={moreRef} onClose={closeMenu}>
              <button
                type="button"
                onClick={() => setMenu('more')}
                className="w-full flex items-center gap-1.5 px-3 pb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-faint hover:text-fg transition-colors duration-150"
              >
                <span aria-hidden>◂</span> Change type
              </button>
              <TypeRows current={block.type} onPick={pickType} />
            </MenuSurface>
          )}
        </div>
      </div>

      <ScratchBody
        initialBody={block.body}
        editable={editable}
        onChange={(body) => onChangeBody(block.id, body)}
      />

      {used && (
        <div className="flex items-center gap-2 mt-1.5">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent/10 text-accent font-mono text-[9px] uppercase tracking-[0.12em] font-medium">
            <span aria-hidden>✓</span> In script
          </span>
          <button
            type="button"
            disabled={actionsDisabled}
            onClick={() => onRestore(block.id)}
            title="Clear the In script tag. The script itself is not touched."
            className="text-[12px] font-medium text-accent hover:underline"
          >
            Restore
          </button>
        </div>
      )}
    </div>
  );
}

export default memo(ScratchBlockCard);

function TypeRows({
  current,
  onPick,
}: {
  current: ScratchBlockType;
  onPick: (type: ScratchBlockType) => void;
}) {
  return (
    <>
      {SCRATCH_BLOCK_TYPES.map((type) => (
        <button
          key={type}
          type="button"
          role="menuitem"
          onClick={() => onPick(type)}
          aria-label={SCRATCH_TYPE_LABEL[type]}
          className={`
            w-full flex items-center gap-2 px-3 py-1.5 text-left
            hover:bg-elevated transition-colors duration-150
            ${type === current ? 'bg-accent/10' : ''}
          `}
        >
          <TypeChip type={type} />
          {type === current && (
            <span className="ml-auto text-accent text-[11px]" aria-hidden>
              ✓
            </span>
          )}
        </button>
      ))}
    </>
  );
}

function MenuIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0 opacity-70"
    >
      {children}
    </svg>
  );
}
